import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import useMySchools from "@/hooks/useMySchools";
import {
  Building2,
  Plus,
  Users,
  ArrowRight,
  Lock,
  Unlock,
  ShieldCheck,
} from "lucide-react";

export default function MySchools() {
  const { memberships, loading } = useMySchools();

  return (
    <div className="dashboard-content">
      {/* Header */}
      <div className="dashboard-welcome">
        <div>
          <h1 className="dashboard-heading">My Schools</h1>
          <p className="dashboard-subheading">
            Manage your school memberships and class sharing settings.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 mt-1">
          <Link to="/schools/join">
            <Button variant="outline" size="sm" className="gap-2">
              <Users size={14} />
              Join School
            </Button>
          </Link>
          <Link to="/schools/create">
            <Button size="sm" className="gap-2">
              <Plus size={14} />
              Create School
            </Button>
          </Link>
        </div>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && memberships.length === 0 && (
        <div className="mt-2">
          <div className="flex flex-col items-center justify-center gap-3 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 size={22} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Not part of any school yet
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-xs">
                Create a school to manage teachers and class sharing, or join
                one using an invite code from your admin.
              </p>
            </div>
          </div>

          {/* CTA cards */}
          <div className="quick-actions-grid">
            <Link to="/schools/create" className="quick-action-card">
              <div className="quick-action-icon">
                <Building2 size={22} />
              </div>
              <div>
                <div className="quick-action-title">Create a School</div>
                <div className="quick-action-desc">
                  Set up a school, invite teachers, and control class sharing
                </div>
              </div>
              <ArrowRight size={16} className="quick-action-arrow" />
            </Link>

            <Link to="/schools/join" className="quick-action-card">
              <div className="quick-action-icon">
                <Users size={22} />
              </div>
              <div>
                <div className="quick-action-title">Join a School</div>
                <div className="quick-action-desc">
                  Enter an invite code from your school admin to join
                </div>
              </div>
              <ArrowRight size={16} className="quick-action-arrow" />
            </Link>
          </div>
        </div>
      )}

      {/* School list */}
      {!loading && memberships.length > 0 && (
        <div className="space-y-3">
          {memberships.map((m) => {
            const school = m.school;
            if (!school) return null;
            return (
              <Link
                key={m._id}
                to={`/schools/${school._id}`}
                className="flex items-center gap-4 bg-card border border-border rounded-xl px-5 py-4 text-foreground no-underline transition-all duration-150 hover:border-primary/60 hover:shadow-sm group"
                style={{ display: "flex" }}
              >
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Building2 size={18} className="text-primary" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground truncate">
                      {school.name}
                    </span>
                    {m.isAdmin && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        <ShieldCheck size={10} />
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      {school.sharingMode === "restricted" ? (
                        <>
                          <Lock size={10} />
                          Restricted sharing
                        </>
                      ) : (
                        <>
                          <Unlock size={10} />
                          Free sharing
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Arrow */}
                <ArrowRight
                  size={15}
                  className="text-muted-foreground group-hover:text-primary transition-colors shrink-0"
                />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
